/**
 * Types shared by the server and the client side of form elements. Pure types/helpers only:
 * nothing in here may import Vue or Node built-ins.
 */

/** Contexts an element can be rendered/used in. */
export type ElementContextKind = 'form' | 'table' | 'search' | 'print' | 'filter';
export const ELEMENT_CONTEXTS: readonly ElementContextKind[] = ['form', 'table', 'search', 'print', 'filter'];

/** Type of the value an element exchanges with the API (`/api/v2/records`). */
export type ElementValueType =
  | 'string'
  | 'text'
  | 'integer'
  | 'number'
  | 'decimal'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'time'
  | 'json'
  | 'list'
  /** display-only element, the column holds no value of its own */
  | 'none';
export const ELEMENT_VALUE_TYPES: readonly ElementValueType[] = [
  'string', 'text', 'integer', 'number', 'decimal', 'boolean', 'date', 'datetime', 'time', 'json', 'list', 'none',
];

export type I18nText = string | Partial<Record<'de' | 'en' | 'fr' | 'nl', string>>;

/** Option of a list-like element (config `options`, legacy "value=label"). */
export interface OptionItem {
  value: string | number;
  label?: I18nText;
}

/** Normalised option: values are always strings (legacy compares strings). */
export interface ElementOption {
  value: string;
  label: I18nText;
}

/** Column config of a form element as stored in etc/tables (core `FormElement`). */
export interface ElementConfig {
  type: string;
  settings?: Record<string, unknown>;
  options?: OptionItem[];
  length?: number;
}

/** What an element knows about the column it is bound to. */
export interface ColumnMeta {
  database: string;
  table: string;
  column: string;
  label?: I18nText;
  /** SQL type from introspection, lower case (`varchar`, `int`, `date` …) */
  sqlType?: string;
  nullable?: boolean;
  /** max length: element `length`, else column size */
  length?: number;
  required?: boolean;
  pseudo?: boolean;
}

export interface TableRef {
  database: string;
  table: string;
}

export interface ColumnTarget extends TableRef {
  column: string;
}

/** Error of an element; `code` is an i18n key (`element.<id>.<code>` or generic `element.<code>`). */
export interface ElementError {
  code: string;
  params?: Record<string, string | number>;
  /** translated message, filled in by the host */
  message?: string;
}

/** Value as delivered by the DB driver. */
export type DbValue = string | number | bigint | boolean | Date | Uint8Array | null;
/** Value written to the DB. */
export type DbWriteValue = string | number | boolean | null;

export type Right = 's' | 'u' | 'i' | 'd';

export function err(code: string, params?: Record<string, string | number>): ElementError {
  return params ? { code, params } : { code };
}

/** Pick a label for the locale (fallback de → en → first). */
export function i18nText(t: I18nText | undefined, locale = 'de'): string {
  if (t == null) return '';
  if (typeof t === 'string') return t;
  const rec = t as Record<string, string | undefined>;
  return rec[locale] ?? rec.de ?? rec.en ?? Object.values(rec).find((x) => x != null) ?? '';
}

/** Normalise config options (value → string, label defaults to the value). */
export function normalizeOptions(options: readonly OptionItem[] | undefined): ElementOption[] {
  return (options ?? []).map((o) => ({ value: String(o.value), label: o.label ?? String(o.value) }));
}

/** Legacy lower-kebab addon id of a legacy type name (`CHECKBOX_NG` → `checkbox-ng`). */
export function legacyId(type: string): string {
  return type.trim().toLowerCase().replace(/_/g, '-');
}
